/**
 * Feature Gap Analysis
 * Croise les besoins BPML (features liées aux lignes enrichies) avec les
 * compétences disponibles dans l'équipe (user_year_feature_scores).
 * Identifie les features non couvertes, les SPOF applicatifs, etc.
 */
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  analyticsApi,
  applicationApi,
  type ApplicationDto,
  type FeatureGapItem,
  type FeatureGapResult,
  type FeatureGapRisk,
} from "../api/api";

// ── Risk helpers ──────────────────────────────────────────────────────────────
const RISK_META: Record<FeatureGapRisk, { label: string; bg: string; text: string; icon: string }> = {
  critical: { label: "Critical",  bg: "#fca5a5", text: "#7f1d1d", icon: "🔴" },
  high:     { label: "High",      bg: "#fdba74", text: "#7c2d12", icon: "🟠" },
  medium:   { label: "Medium",    bg: "#fde68a", text: "#713f12", icon: "🟡" },
  low:      { label: "Covered",   bg: "#bbf7d0", text: "#14532d", icon: "🟢" },
};

function RiskBadge({ risk }: { risk: FeatureGapRisk }) {
  const m = RISK_META[risk];
  return (
    <span style={{
      background: m.bg, color: m.text,
      padding: "2px 8px", borderRadius: 10, fontSize: "0.78em", fontWeight: 700,
      whiteSpace: "nowrap",
    }}>
      {m.icon} {m.label}
    </span>
  );
}

function CoveragePill({ above, total }: { above: number; total: number }) {
  const pct = total > 0 ? Math.round((above / total) * 100) : 0;
  const color = above === 0 ? "#ef4444" : pct >= 60 ? "#16a34a" : "#f59e0b";
  return (
    <span style={{ fontWeight: 700, color, fontSize: "0.9em" }}>
      {above}/{total}
      <span style={{ fontWeight: 400, color: "#94a3b8", fontSize: "0.85em", marginLeft: 4 }}>
        ({pct}%)
      </span>
    </span>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────
export function FeatureGapPage() {
  const { t, i18n } = useTranslation();
  const langFr = i18n.language?.toLowerCase().startsWith("fr");
  const currentYear = new Date().getFullYear();

  const [year, setYear]               = useState(currentYear);
  const [applicationId, setApplicationId] = useState("");
  const [domain, setDomain]           = useState("");
  const [riskFilter, setRiskFilter]   = useState<FeatureGapRisk | "">("");
  const [applications, setApplications] = useState<ApplicationDto[]>([]);
  const [data, setData]               = useState<FeatureGapResult | null>(null);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState("");

  useEffect(() => {
    applicationApi.list().then((r) => setApplications(r.applications)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!applicationId) { setData(null); return; }
    setLoading(true);
    setError("");
    analyticsApi.featureGap({ year, applicationId, domain: domain || undefined })
      .then(setData)
      .catch((e) => setError(e.message ?? "Error"))
      .finally(() => setLoading(false));
  }, [year, applicationId, domain]);

  // Filtered modules
  const filteredModules = useMemo(() => {
    if (!data) return [];
    if (!riskFilter) return data.modules;
    return data.modules.map((mod) => ({
      ...mod,
      features: mod.features.filter((f) => f.risk === riskFilter),
    })).filter((mod) => mod.features.length > 0);
  }, [data, riskFilter]);

  const featureLabel = (f: FeatureGapItem) =>
    langFr && f.featureLabelFr ? f.featureLabelFr : f.featureLabelEn;

  return (
    <div className="sm-page">
      <div className="sm-page-header">
        <h1 className="sm-page-title">{t("feature_gap_title", "Feature Gap Analysis")}</h1>
        <p className="sm-muted">
          {t("feature_gap_sub", "Cross-references BPML feature needs with team application skill scores to identify gaps and risks.")}
        </p>
      </div>

      {/* Filters */}
      <div className="sm-card sm-card-pad" style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 16 }}>
        <label className="sm-label">
          {t("year")}
          <select className="sm-select" value={year} onChange={(e) => setYear(Number(e.target.value))}>
            {[currentYear, currentYear - 1, currentYear - 2].map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </label>

        <label className="sm-label">
          {t("application")}
          <select className="sm-select" value={applicationId}
            onChange={(e) => { setApplicationId(e.target.value); }}>
            <option value="">{t("select_application", "Select application…")}</option>
            {applications.map((a) => (
              <option key={a.id} value={a.id}>{a.label}</option>
            ))}
          </select>
        </label>

        <label className="sm-label">
          {t("domain", "Domain")}
          <input
            className="sm-input"
            style={{ width: 120 }}
            placeholder={t("all_domains", "All domains")}
            value={domain}
            onChange={(e) => setDomain(e.target.value.toUpperCase())}
          />
        </label>

        {data && (
          <label className="sm-label">
            {t("risk_filter", "Risk filter")}
            <select className="sm-select" value={riskFilter}
              onChange={(e) => setRiskFilter(e.target.value as FeatureGapRisk | "")}>
              <option value="">{t("all_risks", "All risks")}</option>
              {(["critical", "high", "medium", "low"] as FeatureGapRisk[]).map((r) => (
                <option key={r} value={r}>{RISK_META[r].icon} {RISK_META[r].label}</option>
              ))}
            </select>
          </label>
        )}
      </div>

      {error && <div className="sm-alert sm-alert-error">{error}</div>}

      {!applicationId && (
        <div className="sm-card sm-card-pad sm-muted" style={{ textAlign: "center", padding: 40 }}>
          {t("select_application_hint", "Select an application to display the gap analysis.")}
        </div>
      )}

      {loading && applicationId && (
        <div className="sm-card sm-card-pad sm-muted" style={{ textAlign: "center", padding: 40 }}>
          {t("loading", "Loading…")}
        </div>
      )}

      {!loading && data && (
        <>
          {/* Summary cards */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 12, marginBottom: 20 }}>
            {(["critical", "high", "medium", "low"] as FeatureGapRisk[]).map((r) => {
              const m = RISK_META[r];
              const count = data.summary[r];
              return (
                <div key={r}
                  className="sm-card sm-card-pad"
                  style={{
                    background: count > 0 ? m.bg : undefined,
                    cursor: "pointer",
                    border: riskFilter === r ? "2px solid #1e40af" : undefined,
                    opacity: riskFilter && riskFilter !== r ? 0.5 : 1,
                  }}
                  onClick={() => setRiskFilter(riskFilter === r ? "" : r)}
                >
                  <div style={{ fontSize: "1.6em", fontWeight: 800, color: count > 0 ? m.text : "#94a3b8" }}>{count}</div>
                  <div style={{ fontSize: "0.8em", fontWeight: 600, color: count > 0 ? m.text : "#94a3b8" }}>
                    {m.icon} {m.label}
                  </div>
                </div>
              );
            })}
            <div className="sm-card sm-card-pad">
              <div style={{ fontSize: "1.6em", fontWeight: 800, color: "#374151" }}>{data.summary.total}</div>
              <div style={{ fontSize: "0.8em", fontWeight: 600, color: "#64748b" }}>
                {t("total_features_needed", "Features needed")}
              </div>
            </div>
            <div className="sm-card sm-card-pad">
              <div style={{ fontSize: "1.6em", fontWeight: 800, color: "#1d4ed8" }}>{data.teamSize}</div>
              <div style={{ fontSize: "0.8em", fontWeight: 600, color: "#64748b" }}>
                {t("team_size", "Team size")}
              </div>
            </div>
          </div>

          {/* Context */}
          <div className="sm-alert sm-alert-info" style={{ marginBottom: 16, fontSize: "0.85em" }}>
            <strong>{data.applicationLabel}</strong> · {data.year}
            {data.domain && <> · {t("domain")}: <strong>{data.domain}</strong></>}
            {" "}— {t("target_label", "Target")}: <strong>≥ {data.target}</strong>
            {" "}— {data.summary.uncovered} {t("uncovered_features", "features with 0 experts above threshold")}
          </div>

          {/* Per-module tables */}
          {filteredModules.map((mod) => (
            <div key={mod.moduleCode ?? "__none__"} className="sm-card" style={{ marginBottom: 16, overflow: "hidden" }}>
              <div style={{ background: "#1e40af", color: "#fff", padding: "8px 16px", fontWeight: 700, fontSize: "0.95em" }}>
                {mod.moduleCode ? `${mod.moduleCode} — ${mod.moduleLabel ?? ""}` : t("no_module", "No module")}
              </div>

              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.82em" }}>
                  <thead>
                    <tr style={{ background: "#f8fafc" }}>
                      <th style={{ textAlign: "left", padding: "6px 12px", minWidth: 200, borderBottom: "1px solid #e2e8f0", position: "sticky", left: 0, background: "#f8fafc", zIndex: 1 }}>
                        {t("feature", "Feature")}
                      </th>
                      <th style={{ padding: "6px 10px", textAlign: "center", borderBottom: "1px solid #e2e8f0", whiteSpace: "nowrap" }}>
                        {t("bpml_lines_col", "BPML lines")}
                      </th>
                      <th style={{ padding: "6px 10px", textAlign: "center", borderBottom: "1px solid #e2e8f0", whiteSpace: "nowrap" }}>
                        🔴 {t("critical_lines_col", "Critical")}
                      </th>
                      <th style={{ padding: "6px 10px", textAlign: "center", borderBottom: "1px solid #e2e8f0", whiteSpace: "nowrap" }}>
                        {t("experts_above_col", "Experts ≥ target")}
                      </th>
                      <th style={{ padding: "6px 10px", textAlign: "center", borderBottom: "1px solid #e2e8f0", whiteSpace: "nowrap" }}>
                        {t("avg_score_col", "Avg score")}
                      </th>
                      <th style={{ padding: "6px 10px", textAlign: "center", borderBottom: "1px solid #e2e8f0", whiteSpace: "nowrap" }}>
                        {t("risk", "Risk")}
                      </th>
                      <th style={{ padding: "6px 10px", textAlign: "left", borderBottom: "1px solid #e2e8f0" }}>
                        {t("concerned_processes_col", "Concerned processes")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {mod.features.map((feat) => {
                      const rowBg = feat.risk === "critical" ? "#fff5f5"
                        : feat.risk === "high" ? "#fffbeb"
                        : feat.risk === "medium" ? "#fffff0"
                        : "#fff";
                      return (
                        <tr key={feat.featureId} style={{ borderBottom: "1px solid #f1f5f9", background: rowBg }}>
                          {/* Feature name */}
                          <td style={{ padding: "6px 12px", position: "sticky", left: 0, background: rowBg, zIndex: 1 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                              <span style={{
                                fontFamily: "monospace", fontSize: "0.85em",
                                background: "#f1f5f9", padding: "1px 5px",
                                borderRadius: 3, color: "#475569", fontWeight: 600,
                              }}>
                                {feat.featureCode}
                              </span>
                              <span style={{ color: "#374151" }}>{featureLabel(feat)}</span>
                            </div>
                            {feat.subModuleCode && (
                              <div style={{ fontSize: "0.75em", color: "#94a3b8", marginTop: 2 }}>
                                {feat.subModuleCode} — {feat.subModuleLabel}
                              </div>
                            )}
                          </td>

                          {/* BPML lines count */}
                          <td style={{ padding: "6px 10px", textAlign: "center", color: "#374151", fontWeight: 600 }}>
                            {feat.bpmlLinesCount}
                          </td>

                          {/* Critical lines */}
                          <td style={{ padding: "6px 10px", textAlign: "center" }}>
                            {feat.criticalLinesCount > 0 ? (
                              <span style={{ fontWeight: 700, color: "#dc2626" }}>{feat.criticalLinesCount}</span>
                            ) : (
                              <span style={{ color: "#94a3b8" }}>—</span>
                            )}
                          </td>

                          {/* Experts above threshold */}
                          <td style={{ padding: "6px 10px", textAlign: "center" }}>
                            <CoveragePill above={feat.expertsAboveThreshold} total={feat.teamSize} />
                          </td>

                          {/* Avg score */}
                          <td style={{ padding: "6px 10px", textAlign: "center", fontWeight: 600,
                            color: feat.avgTeamScore === null ? "#94a3b8"
                              : feat.avgTeamScore >= feat.teamSize ? "#16a34a"
                              : feat.avgTeamScore >= 2 ? "#f59e0b" : "#ef4444" }}>
                            {feat.avgTeamScore !== null ? feat.avgTeamScore.toFixed(1) : "—"}
                          </td>

                          {/* Risk badge */}
                          <td style={{ padding: "6px 10px", textAlign: "center" }}>
                            <RiskBadge risk={feat.risk} />
                          </td>

                          {/* Concerned processes */}
                          <td style={{ padding: "6px 12px", color: "#64748b", fontSize: "0.8em", maxWidth: 280 }}>
                            {feat.concernedProcesses.slice(0, 3).join(" · ")}
                            {feat.concernedProcesses.length > 3 && (
                              <span style={{ color: "#94a3b8" }}> +{feat.concernedProcesses.length - 3}</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ))}

          {filteredModules.length === 0 && (
            <div className="sm-card sm-card-pad sm-muted" style={{ textAlign: "center", padding: 40 }}>
              {t("no_results_for_filter", "No features match the current filter.")}
            </div>
          )}
        </>
      )}
    </div>
  );
}
